/** QuickHouse — Store zustand : projet ouvert, outil actif, sélection, undo/redo, auto-save */
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '../api/client.js';

const clone = (v) => JSON.parse(JSON.stringify(v));
const HISTORY_CAP = 50;

let saveTimer = null;
let transientSnapshot = null; // instantané pris au début d'un drag

export const useProjectStore = create(subscribeWithSelector((set, get) => ({
  // ── État ──
  me: null,                // { username, admin, suite } — identité de suite
  projects: [],
  project: null,
  activeFloorId: null,
  tool: 'select',          // select | wall | door | window | garage | stair | room | delete
  roomMode: 'rect',        // sous-outil Pièce : 'rect' (glisser) | 'poly' (clics)
  stairKind: 'straight',   // sous-outil Escalier : straight | ladder | spiral
  beamKind: 'beam',        // sous-outil Charpente : 'beam' (poutre) | 'post' (poteau)
  openingVariants: { door: 'single', window: 'double', garage: 'roller' }, // variante active par type
  selection: [],           // [{ kind: 'wall'|'opening'|'room', id }] — sélection multiple
  viewMode: '2d',          // '2d' | '3d'
  explode: 0,              // écart d'éclatement 3D (m par niveau)
  walkMode: false,         // visite virtuelle FPS en 3D
  measure3d: false,        // outil de mesure linéaire 3D
  measurePoints: [],       // points cliqués de la mesure 3D (monde, mètres)
  walkHud: null,           // HUD de la visite : { aim: {h, d} | null, heading, pos: {x, y} } (throttlé)
  clip: {                  // plans de coupe 3D : masque au-delà de value (cm) ; flip = autre côté
    x: { on: false, value: null, flip: false },
    y: { on: false, value: null, flip: false },
    z: { on: false, value: null, flip: false },
  },
  hiddenFloors: {},        // floorId → true si masqué en 3D
  view2d: null,            // caméra 2D { panX, panY, zoom } — conservée d'un aller-retour 2D↔3D à l'autre
  chatBusy: false,         // verrou d'édition (réservé aux opérations longues) — toujours faux ici
  rightTab: 'props',       // panneau droit : propriétés uniquement
  textures: [],            // textures importées du projet [{ id, name, url }]
  catalog: [],             // catalogue d'objets (intégré + personnalisé)
  placingId: null,         // objet du catalogue en cours de pose (outil Objet)
  saveState: 'idle',       // idle | saving | saved | error
  statusText: '',
  past: [],
  future: [],

  // ── Identité de suite ──
  async loadMe() {
    try { set({ me: await api('/me') }); } catch { set({ me: null }); }
  },

  // ── Projets ──
  async loadProjects() {
    set({ projects: await api('/projects') });
  },
  /** Partage : fixe la liste des comptes destinataires (propriétaire uniquement). */
  async shareProject(id, sharedWith) {
    await api(`/projects/${id}/share`, { method: 'PUT', body: { sharedWith } });
    await get().loadProjects();
  },
  async createProject(name) {
    const project = await api('/projects', { method: 'POST', body: { name } });
    await get().loadProjects();
    get().openLoaded(project);
  },
  async openProject(id) {
    get().openLoaded(await api(`/projects/${id}`));
  },
  openLoaded(project) {
    set({
      project,
      activeFloorId: project.floors[0]?.id ?? null,
      selection: [], past: [], future: [], tool: 'select', viewMode: '2d',
      hiddenFloors: {}, explode: 0, saveState: 'idle', statusText: '', textures: [],
      view2d: null, // un projet fraîchement ouvert repart de la vue par défaut
    });
    get().loadTextures().catch(() => {});
    get().loadCatalog().catch(() => {});
  },

  // ── Catalogue d'objets ──
  async loadCatalog() {
    set({ catalog: await api('/catalog') });
  },
  setPlacingId: (placingId) => set({ placingId }),

  // ── Textures importées ──
  async loadTextures() {
    const { project } = get();
    if (!project) return;
    set({ textures: await api(`/projects/${project.id}/textures`) });
  },
  async uploadTexture(name, mediaType, data) {
    const { project } = get();
    if (!project) return;
    await api(`/projects/${project.id}/textures`, { method: 'POST', body: { name, mediaType, data } });
    await get().loadTextures();
  },
  async removeTexture(id) {
    const { project } = get();
    if (!project) return;
    await api(`/projects/${project.id}/textures/${id}`, { method: 'DELETE' });
    await get().loadTextures();
  },
  closeProject() {
    clearTimeout(saveTimer);
    set({ project: null, selection: [], past: [], future: [] });
    get().loadProjects();
  },
  async deleteProjectById(id) {
    await api(`/projects/${id}`, { method: 'DELETE' });
    await get().loadProjects();
  },
  /** Recharge depuis le serveur — réinitialise l'historique */
  async reloadProject() {
    const { project } = get();
    if (!project) return;
    const fresh = await api(`/projects/${project.id}`);
    const activeStillThere = fresh.floors.some((f) => f.id === get().activeFloorId);
    set({
      project: fresh,
      past: [], future: [], selection: [],
      activeFloorId: activeStillThere ? get().activeFloorId : (fresh.floors[0]?.id ?? null),
    });
  },

  // ── UI ──
  setTool: (tool) => set({ tool, selection: [] }),
  setRoomMode: (roomMode) => set({ roomMode }),
  setStairKind: (stairKind) => set({ stairKind }),
  setBeamKind: (beamKind) => set({ beamKind }),
  setOpeningVariant: (type, variant) => set((s) => ({ openingVariants: { ...s.openingVariants, [type]: variant } })),
  setViewMode: (viewMode) => set({ viewMode }),
  setSelection: (selection) => set({ selection: selection ?? [] }),
  setActiveFloor: (activeFloorId) => set({ activeFloorId, selection: [] }),
  setExplode: (explode) => set({ explode }),
  setClip: (axis, patch) => set((s) => ({ clip: { ...s.clip, [axis]: { ...s.clip[axis], ...patch } } })),
  setWalkMode: (walkMode) => set({ walkMode, ...(walkMode ? {} : { walkHud: null }) }),
  setMeasure3d: (measure3d) => set({ measure3d, measurePoints: [] }),
  setWalkHud: (walkHud) => set({ walkHud }),
  addMeasurePoint: (p) => set((s) => ({ measurePoints: [...s.measurePoints, p] })),
  clearMeasurePoints: () => set({ measurePoints: [] }),
  toggleFloorHidden: (id) => set((s) => ({ hiddenFloors: { ...s.hiddenFloors, [id]: !s.hiddenFloors[id] } })),
  setView2d: (view2d) => set({ view2d }),
  setStatus: (statusText) => set({ statusText }),

  /** Étage actif (dérivé) */
  activeFloor() {
    const { project, activeFloorId } = get();
    return project?.floors.find((f) => f.id === activeFloorId) ?? null;
  },

  // ── Mutations avec historique ──
  /** Mutation ponctuelle : fn(floors, project) modifie floors en place */
  mutate(fn) {
    const { project, past } = get();
    if (!project || get().chatBusy) return;
    const snapshot = clone(project.floors);
    const floors = clone(project.floors);
    fn(floors, project);
    set({
      project: { ...project, floors },
      past: [...past.slice(-HISTORY_CAP + 1), snapshot],
      future: [],
    });
    get().scheduleSave();
  },
  /** Début d'un drag : capture l'instantané une seule fois */
  beginTransient() {
    if (transientSnapshot || !get().project) return;
    transientSnapshot = clone(get().project.floors);
  },
  /** Pendant un drag : applique sans pousser l'historique */
  applyTransient(fn) {
    const { project } = get();
    if (!project || get().chatBusy) return;
    const floors = clone(project.floors);
    fn(floors, project);
    set({ project: { ...project, floors } });
  },
  /** Fin de drag : pousse l'instantané initial dans l'historique */
  commitTransient() {
    if (!transientSnapshot) return;
    const { past } = get();
    set({ past: [...past.slice(-HISTORY_CAP + 1), transientSnapshot], future: [] });
    transientSnapshot = null;
    get().scheduleSave();
  },
  cancelTransient() {
    if (!transientSnapshot) return;
    const { project } = get();
    set({ project: { ...project, floors: transientSnapshot } });
    transientSnapshot = null;
  },

  undo() {
    const { project, past, future } = get();
    if (!past.length || !project) return;
    const prev = past[past.length - 1];
    set({
      project: { ...project, floors: prev },
      past: past.slice(0, -1),
      future: [clone(project.floors), ...future],
      selection: [],
    });
    get().scheduleSave();
  },
  redo() {
    const { project, past, future } = get();
    if (!future.length || !project) return;
    const [next, ...rest] = future;
    set({
      project: { ...project, floors: next },
      past: [...past, clone(project.floors)],
      future: rest,
      selection: [],
    });
    get().scheduleSave();
  },

  // ── Sauvegarde automatique (débouncée) ──
  scheduleSave() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => get().saveNow(), 800);
  },
  async saveNow() {
    const { project } = get();
    if (!project) return;
    set({ saveState: 'saving' });
    try {
      await api(`/projects/${project.id}`, { method: 'PUT', body: project });
      set({ saveState: 'saved' });
    } catch (e) {
      set({ saveState: 'error', statusText: `Sauvegarde impossible : ${e.message}` });
    }
  },
})));
